// US 1288679 — Feedback (My Voice) dynamic layout without reopening the XSS
// remediation from US 1280933 / finding #12996 (AC-3).
//
// The feedback layout CSS is stored in the database as STRUCTURED JSON (a
// selector -> { property: value } map) instead of a raw <style> HTML blob.
// This module turns that JSON into a stylesheet string that is rendered by the
// component as a React <style> TEXT node (NOT dangerouslySetInnerHTML), so the
// untrusted-HTML injection path flagged by the pentest is never used.
//
// Defense in depth — every selector, property and value is allow-listed:
//   - selectors may only contain safe CSS selector characters (blocks { } < > ;
//     breakout so DB data cannot escape its rule and inject arbitrary CSS/HTML),
//   - properties must be letters/hyphens only,
//   - values are rejected if they contain url(), expression(), javascript:,
//     vbscript:, @import or structural breakout characters.
// Anything that fails validation is dropped (fail-closed) rather than rendered.

export type CssStyleMap = Record<string, Record<string, string>>;

// Safe CSS selector characters only. Intentionally excludes { } ; < > and @ so
// database-supplied selectors cannot break out of their rule or start at-rules.
const SAFE_SELECTOR = /^[a-zA-Z0-9\s.#:_\-[\]="'()>~+*,]+$/;

// CSS property names: letters and hyphens only (e.g. margin-top, box-sizing).
const SAFE_PROPERTY = /^[a-zA-Z-]+$/;

// Dangerous value tokens: CSS-based exfiltration / execution vectors plus the
// structural breakout characters < > { } ; and the backslash. Backslashes are
// rejected because CSS escape sequences (e.g. u\72 l) can reconstruct blocked
// tokens like url() after the browser decodes them; no legitimate value here
// needs a backslash.
const UNSAFE_VALUE = /\\|url\s*\(|expression\s*\(|javascript\s*:|vbscript\s*:|@import|[<>{};]/i;

/**
 * Build a stylesheet string from a structured JSON style map.
 * Returns '' for anything that is not a usable object (fail-closed).
 *
 * @param styles A selector -> { property: value } map (usually from the DB).
 * @returns A CSS string safe to render as a React <style> text node.
 */
export function buildCssFromJson(styles: unknown): string {
    if (!styles || typeof styles !== 'object' || Array.isArray(styles)) {
        return '';
    }

    const rules: string[] = [];

    for (const [selector, declarations] of Object.entries(styles as CssStyleMap)) {
        if (typeof selector !== 'string' || !SAFE_SELECTOR.test(selector)) {
            continue;
        }
        if (!declarations || typeof declarations !== 'object' || Array.isArray(declarations)) {
            continue;
        }

        const body = Object.entries(declarations)
            .filter(([property, value]) =>
                typeof property === 'string' &&
                typeof value === 'string' &&
                SAFE_PROPERTY.test(property) &&
                !UNSAFE_VALUE.test(value)
            )
            .map(([property, value]) => `${property}:${value.trim()}`)
            .join(';');

        if (body) {
            rules.push(`${selector}{${body}}`);
        }
    }

    return rules.join('\n');
}

// Generated by Copilot
// updated on: 2026-08-03 | 78 lines added | [Auto Generated] | US 1288679: build validated feedback CSS from DB JSON (no dangerouslySetInnerHTML) so layout stays dynamic without reopening #12996 | Time Taken 10 min
